import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { BedDouble, Users, Anchor, Star } from "lucide-react";
import { MaskedHeading } from "@/components/site/MaskedHeading";
import { bookOnVrbo } from "@/lib/booking";
import { PROPERTY, HERO_IMAGE } from "@/data/property";

const stats = [
  { icon: BedDouble, label: "6 Bedrooms" },
  { icon: Users, label: "Sleeps 18" },
  { icon: Anchor, label: "Private Dock" },
];

export function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.4]);

  return (
    <section id="top" ref={ref} className="relative h-[100svh] min-h-[640px] w-full overflow-hidden">
      <motion.img
        src={HERO_IMAGE.src}
        alt={HERO_IMAGE.alt}
        style={{ y, scale }}
        className="absolute inset-0 h-full w-full object-cover will-change-transform"
      />
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/40"
      />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/85 via-black/35 to-transparent" />

      <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-16 md:pb-24">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="mb-6 flex items-center gap-3 text-white/85"
        >
          <span className="h-px w-10 bg-white/50" />
          <span className="text-xs md:text-sm uppercase tracking-[0.22em]" data-testid="hero-overline">
            {PROPERTY.region} · {PROPERTY.location}
          </span>
        </motion.div>

        <h1 className="font-display text-white text-shadow-hero text-6xl sm:text-7xl lg:text-[8.5rem] leading-[0.86] tracking-tight" data-testid="hero-title" aria-label="PK Lake House">
          <MaskedHeading lines={["PK Lake", <em key="h" className="italic font-light">House</em>]} delay={0.35} />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-6 max-w-xl text-lg md:text-xl text-white/90 font-light text-shadow-hero"
        >
          {PROPERTY.tagline}.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-3 text-white/90"
        >
          {stats.map((s) => (
            <span key={s.label} className="inline-flex items-center gap-2 text-sm md:text-base">
              <s.icon className="h-4 w-4 text-white/70" strokeWidth={1.5} />
              {s.label}
            </span>
          ))}
          <span className="inline-flex items-center gap-1.5 text-sm md:text-base">
            <Star className="h-4 w-4 fill-white text-white" strokeWidth={1.5} />
            {PROPERTY.rating}/10 · {PROPERTY.reviewCount} reviews
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => bookOnVrbo("hero")}
            data-testid="hero-book-button"
            className="group inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-medium tracking-wide text-primary transition-all duration-300 hover:gap-3.5 hover:bg-white/90"
          >
            Book on VRBO
            <span aria-hidden className="transition-transform group-hover:translate-x-0.5">&rarr;</span>
          </button>
          <a
            href="#gallery"
            data-testid="hero-explore-link"
            className="inline-flex items-center gap-2 rounded-full border border-white/40 px-8 py-4 text-sm font-medium tracking-wide text-white transition-all duration-300 hover:bg-white/10"
          >
            Explore the house
          </a>
        </motion.div>
      </div>
    </section>
  );
}
